import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import HoloTable from '../../components/HoloTable'
import { Link } from 'react-router-dom'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { getFilterOperators } from '../../utilities/gridUtils'
import setUrlParams from '../../utilities/setUrlParams'

const SelectAnalysisChainsModal = ({
  open,
  onClose,
  onSave,
  selectedChains = [],
}) => {
  const [dataTypes, setDataTypes] = useState([])
  const [analyses, setAnalyses] = useState([])
  const [analysisChains, setAnalysisChains] = useState([])
  const [totalChainsCount, setTotalChainsCount] = useState()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const [sortModel, setSortModel] = useState([
    { field: 'createdAt', sort: 'desc' },
  ])
  const [filterModel, setFilterModel] = useState({ items: [] })

  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(selectedChains.map((chain) => chain.uuid)),
  })

  const prevSelectedChainsRef = useRef(selectedChains)

  useEffect(() => {
    const hasSelectedChainsChanged =
      selectedChains.length !== prevSelectedChainsRef.current.length ||
      !selectedChains.every(
        (chain, index) =>
          chain.uuid === prevSelectedChainsRef.current[index]?.uuid,
      )

    if (hasSelectedChainsChanged) {
      setRowSelectionModel({
        type: 'include',
        ids: new Set(selectedChains.map((chain) => chain.uuid)),
      })
      prevSelectedChainsRef.current = selectedChains
    }
  }, [selectedChains])

  useEffect(() => {
    const fetchData = async () => {
      if (!open) return

      try {
        const params = setUrlParams(
          paginationModel.page,
          paginationModel.pageSize,
          sortModel,
          filterModel,
        )

        const [dataTypesResponse, analysesResponse, analysisChainsResponse] =
          await Promise.all([
            fetchWithAuth('data-types'),
            fetchWithAuth('analyses'),
            fetchWithAuth(`analysis-chains?${params.toString()}`),
          ])

        if (dataTypesResponse.data) {
          const newDataTypes = dataTypesResponse.data.data
          if (
            dataTypes.length !== newDataTypes.length ||
            !dataTypes.every(
              (dt, index) => dt.uuid === newDataTypes[index]?.uuid,
            )
          ) {
            setDataTypes(newDataTypes)
          }
        }
        if (analysesResponse.data) {
          const newAnalyses = analysesResponse.data.data
          if (
            analyses.length !== newAnalyses.length ||
            !analyses.every(
              (analysis, index) => analysis.uuid === newAnalyses[index]?.uuid,
            )
          ) {
            setAnalyses(newAnalyses)
          }
        }
        if (analysisChainsResponse.data) {
          const newAnalysisChains = analysisChainsResponse.data.data
          if (
            analysisChains.length !== newAnalysisChains.length ||
            !analysisChains.every(
              (chain, index) => chain.uuid === newAnalysisChains[index]?.uuid,
            )
          ) {
            setAnalysisChains(newAnalysisChains)
            setTotalChainsCount(
              analysisChainsResponse.data.pagination.totalItems,
            )
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchData()
  }, [
    open,
    dataTypes,
    analyses,
    analysisChains,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filterModel,
  ])

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        filterable: true,
        filterOperators: getFilterOperators('string'),
        sortable: true,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        filterable: false,
        filterOperators: getFilterOperators('string'),
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'is_basic',
        headerName: 'Is Basic',
        flex: 1,
        filterable: false,
        sortable: false,
        type: 'boolean',
        disableColumnMenu: true,
      },
      {
        field: 'is_auto_compatible',
        headerName: 'Is Auto Compatible',
        flex: 1,
        filterable: true,
        sortable: true,
        type: 'singleSelect',
        filterOperators: getFilterOperators('boolean', true),
        valueOptions: ['true', 'false'],
      },
      {
        field: 'analysis',
        headerName: 'Analyses',
        flex: 3,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        filterOperators: getFilterOperators('array', true),
        valueOptions:
          analyses?.map((analysis) => ({
            value: analysis.uuid,
            label: analysis.name,
          })) || [],

        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {params.row?.analyses?.map((analysis, index) => (
              <Chip
                key={analysis.uuid}
                label={`${index + 1}. ${analysis.name}`}
                component={Link}
                to={`/analyses/${analysis.uuid}`}
                variant="outlined"
                clickable
                sx={{
                  m: 1,
                }}
              />
            ))}
          </Box>
        ),
      },
      {
        field: 'lastAnalysisName',
        headerName: 'Final Analysis',
        flex: 3,
        sortable: true,
        filterable: false,
        valueGetter: (params) => params?.row?.lastAnalysisName || '',
        renderCell: (params) => {
          const lastAnalysis =
            params.row?.analyses?.[params.row.analyses.length - 1]
          return lastAnalysis ? (
            <Chip
              label={lastAnalysis.name}
              component={Link}
              to={`/analyses/${lastAnalysis.uuid}`}
              variant="outlined"
              clickable
              sx={{ m: 1 }}
            />
          ) : null
        },
      },
      {
        field: 'input_datatype_ids',
        headerName: 'Input Data Types',
        flex: 4,
        sortable: false,
        filterable: true,
        type: 'singleSelect',
        filterOperators: getFilterOperators('array', true),
        valueOptions:
          dataTypes?.map((dt) => ({
            value: dt.uuid,
            label: dt.name,
          })) || [],
        valueGetter: (params) =>
          (params &&
            params.row?.inputDataTypes?.map((dt) => dt.name).join(', ')) ||
          '',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {params.row?.inputDataTypes?.map((dataType) => (
              <Chip
                key={dataType.uuid}
                label={dataType.name}
                component={Link}
                to={`/data-types/${dataType.uuid}`}
                clickable
                variant="outlined"
                sx={{
                  m: 1,
                }}
              />
            ))}
          </Box>
        ),
      },
    ]
  }, [analyses, dataTypes])

  const handleSave = useCallback(() => {
    onSave(rowSelectionModel)
    onClose()
  }, [onSave, onClose, rowSelectionModel])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogContent>
        <HoloTable
          id="analysis-chains"
          data={analysisChains}
          columns={columns}
          onRowClick={() => {}}
          rowCount={totalChainsCount}
          checkboxSelection={true}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel)
          }}
          rowSelectionModel={rowSelectionModel}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          disableDensitySelector
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Select chains
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SelectAnalysisChainsModal
